import React from 'react';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@mui/material';
import LocalizedStrings from 'react-localization';
import { localisedText } from '../../lib/localisation.es6';
import { updateCurrentLanguage } from '../../lib/languageUtils.es6';
import BoxInc from '../../shared-ui/BoxInc/Index';
import ButtonInc from '../../shared-ui/ButtonInc';
import { SubscriptionsTable } from './components/SubscriptionsTable';
import { AddEditSubscription } from './components/AddEditSubscription';
import {
  getAllWebhookSubscriptionsRequest,
  createWebhookSubscriptionRequest,
  editWebhookSubscriptionRequest,
  deleteWebhookSubscriptionRequest,
  resetCreateWebhookSuccess,
  resetEditWebhookSuccess,
  saveWebhookSubscriptionAuthRequest,
  deleteWebhookSubscriptionAuthRequest,
  resetAuthWebhookSuccess,
  resetAuthWebhookError,
} from './actions.es6';

const labels = new LocalizedStrings(localisedText);

export class WebhookSubscriptions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      labels: new LocalizedStrings(localisedText),
      openDialog: false,
      openDeleteDialog: false,
      selectedSubscription: null,
      subscriptionToDelete: null,
      snackbarMessage: '',
      snackbarType: 'success',
      showSnackbar: false,
    };
  }

  componentWillMount() {
    this.updateLanguage();
    this.props.dispatch(getAllWebhookSubscriptionsRequest());
  }

  componentDidUpdate(prevProps) {
    this.updateLanguage();
    const { createSuccess, editSuccess, deleteSuccess, authSuccess, authErrors } = this.props;

    if (createSuccess && !prevProps.createSuccess) {
      this.showSnackbar(labels.webhookCreatedSuccess, 'success');
      this.props.dispatch(resetCreateWebhookSuccess());
      this.closeDialog();
    }

    if (editSuccess && !prevProps.editSuccess) {
      this.showSnackbar(labels.webhookUpdatedSuccess, 'success');
      this.props.dispatch(resetEditWebhookSuccess());
      this.closeDialog();
    }

    if (deleteSuccess && !prevProps.deleteSuccess) {
      this.showSnackbar(labels.webhookDeletedSuccess, 'success');
      this.closeDeleteDialog();
    }

    if (authSuccess && !prevProps.authSuccess) {
      this.showSnackbar(labels.webhookAuthUpdatedSuccess || 'Auth configuration updated successfully', 'success');
      this.props.dispatch(resetAuthWebhookSuccess());
    }

    if (authErrors && !prevProps.authErrors) {
      this.showSnackbar(authErrors, 'error');
      this.props.dispatch(resetAuthWebhookError());
    }
  }

  updateLanguage = () => {
    updateCurrentLanguage(
      this.props.globalData.language,
      'WebhookSubscriptions',
      this.state.labels
    );
  };

  showSnackbar = (message, type = 'success') => {
    this.setState({ snackbarMessage: message, snackbarType: type, showSnackbar: true });
    setTimeout(() => this.setState({ showSnackbar: false }), 3000);
  };

  openDialog = (subscription = null) => {
    this.setState({ openDialog: true, selectedSubscription: subscription });
  };

  closeDialog = () => {
    this.setState({ openDialog: false, selectedSubscription: null });
  };

  openDeleteDialog = (subscription) => {
    this.setState({ openDeleteDialog: true, subscriptionToDelete: subscription });
  };

  closeDeleteDialog = () => {
    this.setState({ openDeleteDialog: false, subscriptionToDelete: null });
  };

  handleCreateOrEdit = (payload, isEdit) => {
    if (isEdit) {
      const { guid, ...rest } = payload;
      this.props.dispatch(editWebhookSubscriptionRequest({ guid, ...rest }));
    } else {
      this.props.dispatch(createWebhookSubscriptionRequest(payload));
    }
  };

  handleDelete = (subscription) => {
    this.props.dispatch(deleteWebhookSubscriptionRequest(subscription.guid));
  };

  handleSaveAuth = (guid, authConfig) => {
    this.props.dispatch(saveWebhookSubscriptionAuthRequest(guid, authConfig));
  };

  handleRemoveAuth = (guid) => {
    this.props.dispatch(deleteWebhookSubscriptionAuthRequest(guid));
  };

  render() {
    const {
      subscriptions = [],
      loader,
      createErrors,
      editErrors,
      deleteErrors,
      createLoader,
      editLoader,
      deleteLoader,
      authLoader,
      authErrors,
    } = this.props;

    const {
      openDialog,
      openDeleteDialog,
      selectedSubscription,
      subscriptionToDelete,
      showSnackbar,
      snackbarMessage,
      snackbarType,
    } = this.state;

    const isLoading = createLoader || editLoader;
    const hasErrors = createErrors || editErrors || deleteErrors;
    const errorMessage = createErrors || editErrors || deleteErrors;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <BoxInc
            expanded
            title={labels.webhook || 'Webhooks'}
            actionButtons={[
              <ButtonInc
                variant="contained"
                color="primary"
                size="large"
                onClick={() => this.props.dispatch(getAllWebhookSubscriptionsRequest())}
              >
                {labels.refresh || 'Refresh'}
              </ButtonInc>,
              <ButtonInc
                variant="contained"
                color="primary"
                size="large"
                onClick={() => this.openDialog()}
              >
                {labels.addWebhook || 'Add Webhook'}
              </ButtonInc>,
            ]}
          >
            {showSnackbar && (
              <Alert severity={snackbarType} style={{ marginBottom: 16 }}>
                {snackbarMessage}
              </Alert>
            )}

            <SubscriptionsTable
              subscriptions={subscriptions}
              loading={loader}
              onEdit={this.openDialog}
              onDelete={this.openDeleteDialog}
              onSaveAuth={this.handleSaveAuth}
              onRemoveAuth={this.handleRemoveAuth}
              authLoader={authLoader}
              authErrors={authErrors}
            />

            <AddEditSubscription
              open={openDialog}
              onClose={this.closeDialog}
              subscription={selectedSubscription}
              loading={isLoading}
              errors={hasErrors ? errorMessage : null}
              onSave={this.handleCreateOrEdit}
            />

            <Dialog
              open={openDeleteDialog}
              onClose={this.closeDeleteDialog}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>{labels.confirm || 'Confirm'}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {labels.deleteWebhookConfirm || 'Are you sure you want to delete this webhook subscription?'}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={this.closeDeleteDialog} disabled={deleteLoader}>
                  {labels.cancel || 'Cancel'}
                </Button>
                <Button
                  onClick={() => this.handleDelete(subscriptionToDelete)}
                  disabled={deleteLoader}
                  color="secondary"
                  variant="contained"
                >
                  {deleteLoader ? <CircularProgress size={24} /> : labels.delete || 'Delete'}
                </Button>
              </DialogActions>
            </Dialog>
          </BoxInc>
        </Grid>
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({
  subscriptions: state.webhookSubscriptions.subscriptions,
  loader: state.webhookSubscriptions.loader,
  createLoader: state.webhookSubscriptions.createLoader,
  createSuccess: state.webhookSubscriptions.createSuccess,
  createErrors: state.webhookSubscriptions.createErrors,
  editLoader: state.webhookSubscriptions.editLoader,
  editSuccess: state.webhookSubscriptions.editSuccess,
  editErrors: state.webhookSubscriptions.editErrors,
  deleteLoader: state.webhookSubscriptions.deleteLoader,
  deleteSuccess: state.webhookSubscriptions.deleteSuccess,
  deleteErrors: state.webhookSubscriptions.deleteErrors,
  authLoader: state.webhookSubscriptions.authLoader,
  authSuccess: state.webhookSubscriptions.authSuccess,
  authErrors: state.webhookSubscriptions.authErrors,
  globalData: state.global,
});

export default connect(mapStateToProps)(WebhookSubscriptions);
